// Single source of truth for quote status across Quote Builder, View Quotes,
// the expanded row and the Status filter. One set of values, one set of
// colours, one control. Status is the ONLY lifecycle field — Payment/Sent are
// just audit stamps kept in sync for the "Paid this month" KPI and SLA clock.
import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";

// The 7 canonical statuses. Archived is terminal and hides a quote from lists.
export const QUOTE_STATUSES = ["Draft", "Sent", "Accepted", "Rejected", "Paid", "Expired", "Archived"];

// Which stamps a status implies (Paid is a distinct terminal "won & paid" state).
export const STATUS_FLAGS = {
  Draft:    { sent: false, paid: false },
  Sent:     { sent: true,  paid: false },
  Accepted: { sent: true,  paid: false },
  Rejected: { sent: true,  paid: false },
  Expired:  { sent: true,  paid: false },
  Paid:     { sent: true,  paid: true  },
};

// Colours for the dropdown menu (CSS-var / hex — used by QuoteStatusMenu).
export const STATUS_META = {
  Draft:    { colour: "var(--t2-grey1)",   dot: "#94a3b8" },
  Sent:     { colour: "var(--t2-orange)",  dot: "#ff6820" },
  Accepted: { colour: "var(--t2-teal)",    dot: "#2dd4bf" },
  Rejected: { colour: "#f87171",           dot: "#f87171" },
  Paid:     { colour: "#34d399",           dot: "#34d399" },
  Expired:  { colour: "#fbbf24",           dot: "#fbbf24" },
  Archived: { colour: "var(--t2-grey2)",   dot: "#6b7280" },
};

// Tailwind pill classes (View Quotes table) — tuned to match STATUS_META.
export const DOC_PILL = {
  Draft:    "bg-slate-500/15 text-slate-300 border-slate-500/30",
  Sent:     "bg-orange-500/15 text-orange-300 border-orange-500/30",
  Accepted: "bg-teal-500/15 text-teal-300 border-teal-500/30",
  Rejected: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  Paid:     "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Expired:  "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Archived: "bg-zinc-700/20 text-zinc-400 border-zinc-600/30",
};

// Normalise ANY historic free-text status (+ optional Payment/Sent flags) onto
// one of the 7 canonical values. Mirror of backend `_canonical_status`.
export function canonicalStatus(status, { payment, sent } = {}) {
  const t = String(status || "").trim().toLowerCase();
  if (t === "archived") return "Archived";
  if ((payment && payment.paid) || t.includes("paid")) return "Paid";
  if (["won", "accept", "ordered"].some((k) => t.includes(k))) return "Accepted";
  if (["lost", "reject", "frozen", "declin"].some((k) => t.includes(k))) return "Rejected";
  if (t.includes("expire")) return "Expired";
  if (["sent", "negoti", "waiting", "hold", "no answer", "chase"].some((k) => t.includes(k)) || (sent && sent.sent)) return "Sent";
  return "Draft";
}

export const docClass = (s, opts) => DOC_PILL[canonicalStatus(s, opts)] || DOC_PILL.Draft;

// Build the {Status, Sent, Payment} patch for a status change — keeps the
// legacy flags in sync exactly like Quote Builder does.
export function statusChangePatch(next, quote, markedBy) {
  const f = STATUS_FLAGS[next] || { sent: false, paid: false };
  const stamp = { markedBy: markedBy || "staff", markedAt: new Date().toISOString() };
  return {
    Status: next,
    Sent: f.sent
      ? { ...(quote.Sent || {}), sent: true, ...(quote.Sent?.sent ? {} : stamp) }
      : { sent: false, markedBy: null, markedAt: null },
    Payment: f.paid
      ? { ...(quote.Payment || {}), paid: true, ...(quote.Payment?.paid ? {} : stamp) }
      : { paid: false, markedBy: null, markedAt: null },
  };
}

// The shared status control (pill button + dropdown). Identical in every screen.
export function QuoteStatusMenu({ value, onChange, disabled, flash = false, openSignal = 0 }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { if (openSignal && !disabled) setOpen(true); }, [openSignal, disabled]);
  const meta = STATUS_META[value] || STATUS_META.Draft;
  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        data-testid="quote-status-menu"
        className={`qs-pill qs-${value}${flash ? " qs-flash" : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        title={disabled ? "Add a customer / save the quote first" : "Change quote status"}
        style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "6px 12px", borderRadius: 9,
          border: `1px solid ${meta.colour}`, background: `${meta.dot}1a`,
          color: meta.colour, fontSize: 15, fontWeight: 800, letterSpacing: "-.01em",
          cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.55 : 1,
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: meta.dot, display: "inline-block" }} />
        {value}
        <ChevronDown className="w-4 h-4" />
      </button>
      {open && !disabled && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 120 }} data-testid="quote-status-backdrop" />
          <div role="menu" data-testid="quote-status-dropdown"
            style={{
              position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 121,
              minWidth: 200, background: "var(--t2-surface)", border: "1px solid var(--t2-border)",
              borderRadius: 10, boxShadow: "0 12px 32px rgba(0,0,0,0.5)", padding: 5,
            }}>
            {QUOTE_STATUSES.map((s) => {
              const m = STATUS_META[s];
              const active = s === value;
              return (
                <button key={s} type="button" role="menuitem"
                  onClick={() => { setOpen(false); onChange(s); }}
                  data-testid={`quote-status-opt-${s}`} className={`qs-opt-${s}`}
                  style={{
                    display: "flex", alignItems: "center", gap: 9, width: "100%",
                    padding: "8px 12px", border: "none", borderRadius: 7, cursor: "pointer",
                    background: active ? "var(--t2-surface3)" : "transparent",
                    color: m.colour, fontSize: 12.5, fontWeight: 700, textAlign: "left",
                  }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: m.dot, display: "inline-block" }} />
                  {s}{s === "Archived" ? "  ·  hides from lists" : ""}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
